import { IdTargetCount } from "./CommonRenderTargets";
import { USE_OVERLAY_DEPTH_COPY } from "../globals";
import { logger } from "../../logger/Logger";
import { errorCodeString, ErrorCodes } from "../../file-loaders/net/ErrorCodes";
import { isMobileDevice, isNodeJS } from "../../compat";
import THREE from "three";
import { getGlobal } from "../../compat";
import { colorToABGR } from "./main/uniforms/ObjectUniforms";
import { CategoryHighlightingCompare } from "./post/BlendSettings";

/** @import { Renderer } from './Renderer' */
/** @import { RenderModel } from '../scene/RenderModel' */

const THREE_WHITE = new THREE.Color(0xffffff);

export function cubicBezier(p, t) {
  //var cx = 3.0 * p[0];
  //var bx = 3.0 * (p[2] - p[0]) - cx;
  //var ax = 1.0 - cx -bx;
  var cy = 3.0 * p[1];
  var by = 3.0 * (p[3] - p[1]) - cy;
  var ay = 1.0 - cy - by;

  //return ((ax * t + bx) * t + cx) * t;
  return ((ay * t + by) * t + cy) * t;
}


export class RenderContextWebGPU {
  /** @type {Renderer} */
  _renderer = undefined;

  //The camera and lights used for an entire progressive pass (potentially several GL frames)
  _camera = undefined;
  _lights = undefined;

  _lastIdAtPixelsResults = {};

  _exposure = 0.0;
  _exposureBias = 0.0;
  _envRotation = 0.0;
  _tonemapMethod = 0;
  _unitScale = 1.0;

  _w = undefined;
  _h = undefined;
  _warnedLeak = false;

  // An offscreen context avoids affecting the main canvas Rendering
  _isOffscreen = false;

  _idReadbackBuffers = {};
  _modelIdReadbackBuffers = {};
  _idRes = [0, 0]; // Reused in rolloverObjectViewport

  _clearAlpha = 1.0;
  _useOverlayAlpha = 1.0;
  _isWeakDevice = false;

  // Smooth fade-in of roll-over highlighting
  _lastObjTime = 0;
  _lastHighlightId = 0;
  _lastHighlightModelId = 0;
  _lastObjChanged = false;

  _clearCategoryOnRollover = false;
  _easeCurve = [0.42, 0, 1, 1];
  _easeSpeed = 0.004; // 1/ms duration of the fade-in, i.e., 0.004 => ~250ms
  _rollOverFadeEnabled = true;

  // Independent fade-in for category highlighting
  _lastCategoryTime = 0;

  //Rendering options
  settings = {
    antialias: true,
    sao: false,
    useHdrTarget: false,
    haveTwoSided: false,
    //useSSAA: false, // WebGL only
    //idbuffer: true, // WebGL only
    customPresentPass: false,
    envMapBg: false,
    // The number of id targets (2 by default, 3 when category highlighting is enabled).
    // Updated dynamically via setCategoryHighlightingEnabled().
    numIdTargets: IdTargetCount,
    renderEdges: false,
    // useIdBufferSelection: false, // WebGL only
    copyDepth: false, // whether to use depth buffer copying instead of sharing
    swapBlackAndWhite: false // WebGPU only: whether to swap black and white on sheets
  };

  _oldSettings = {};

  // If a target is set (default null), the final frame is rendered into this._offscreenTarget instead of the canvas.
  _offscreenTarget = null;

  _isInitialized = false;

  _userFinalPass = null;

  // see waitForIdReadback
  _waitForIdReadbackPromise = null; // null|Promise

  isWeakDevice() {
    return this._isWeakDevice;
  }

  // @param {Renderer}             webGpuRenderer
  // @param {number}               width, height - render target extents
  // @param {object}               [options]
  // @param {bool}                 [options.offscreen] - By default (false), we render into the canvas of WebGLRenderer. If true, we render into an offscreen target instead - without affecting the main canvas.
  //                                                                Note: This flag is only relevant for 3D. For 2D, we always use idBufferSelection if we have an idBuffer.
  init(webGPURenderer, width, height, options = {}) {

    webGPURenderer.initSync(width, height);

    this._isWeakDevice = isMobileDevice();

    this._w = width;
    this._h = height;

    this._renderer = webGPURenderer;

    this._isOffscreen = !!options.offscreen;
    this.settings.copyDepth = USE_OVERLAY_DEPTH_COPY;

    //delayed until first begin frame
    //this.initPostPipeline(this.settings.sao, this.settings.antialias);
  }

  /** @returns {Renderer} */
  getRenderer() {
    return this._renderer;
  }

  setDepthMaterialOffset(on, factor, units) {
  }

  setUserFinalPass(pass) {
    this._userFinalPass = pass;
  }

  // Fades rollover and category highlighting independently via separate timers so that triggering one does not
  // restart the other's animation.
  overlayUpdate() {
    if (this._lastObjChanged) {
      this._lastObjChanged = false;
      return true;
    }

    const bs = this._renderer.getBlendSettings();
    const hasRollover = this._lastHighlightId !== 0 && this._lastHighlightId !== -1;
    const hasCategory = bs.getCategoryHighlightingValue() !== 0;

    if (!hasRollover && !hasCategory) return false;

    const now = performance.now();
    const easeFrom = (t0) => this._rollOverFadeEnabled ? cubicBezier(this._easeCurve, Math.min((now - t0) * this._easeSpeed, 1.0)) : 1.0;

    let changed = false;
    if (hasRollover) {
      const v = easeFrom(this._lastObjTime);
      if (bs.getHighlightIntensity() !== v) {bs.setHighlightIntensity(v);changed = true;}
    }
    if (hasCategory) {
      const v = easeFrom(this._lastCategoryTime);
      if (bs.getCategoryHighlightIntensity() !== v) {bs.setCategoryHighlightIntensity(v);changed = true;}
    }
    return changed;
  }

  // Enable/Disable smooth fading of roll-over highlight intensity.
  setRollOverFadeEnabled(enabled) {
    this._rollOverFadeEnabled = enabled;
  }

  // clear the color target and other targets, as needed
  beginScene(prototypeScene, camera, customLights, needClear) {
    this._camera = camera;
    this._lights = customLights;
    this._lastIdAtPixelsResults = {};

    if (!this._isInitialized && this._w) {
      this.initPostPipeline(this.settings.sao, this.settings.antialias);
      this._isInitialized = true;
    } else if (!this._w) {
      if (!this._warnedLeak && !isNodeJS()) {
        logger.error("Rendering to a canvas that was resized to zero. If you see this message you may be accidentally leaking a viewer instance.", errorCodeString(ErrorCodes.VIEWER_INTERNAL_ERROR));
        this._warnedLeak = true;
      }
      return;
    }

    //We need to render once with the "prototype" scene which
    //only contains the cameras and lights, so that their positions
    //and transforms get updated to the latest camera. Hence the
    //call to render instead of just clear.


    //Clear the color target
    if (needClear) {

      this._renderer.getEnvMapPass().setCamera(this._camera);

      this._renderer.renderBackground(this.settings.envMapBg);

      this._renderer.clearMainTargets();

      //Done when rendering overlays
      //this._renderer.clearOverlayTargets();
    }

    if (!this.settings.sao) {
      // Ensure that any previous SSAO computation post-process target is not blended in.
      // This looks redundant with computeSSAO()'s code setting this blend off. However, it's
      // possible for computeSSAO() to not be executed if (a) smooth navigation and AO are both on
      // and (b) the scene is moving. In that case, smooth navigation turns off AO entirely in
      // Viewer3DImpl.js and computSSAO() is never called at all.
      this._renderer.getBlendSettings().setAOEnabled(false);
    }

    // Render the prototype/pre-model scene, which may also contain some user added custom geometry.
    // The key bit here is the "updateLights" true flag, which updates the lights for the scene; this is the
    // only place this flag is passed in as true.
    this._renderer.beginScene(this._camera, this._lights);
  }

  // Called incrementally by the scene traversal, potentially
  // across several frames.
  renderScenePart(scene, want_colorTarget, want_saoTarget, want_idTarget) {

    this._lastIdAtPixelsResults = {};

    this._renderer.renderScenePart(scene, this.settings.renderEdges);
  }

  clearAllOverlays() {
    this._renderer.clearOverlayTargets();
  }

  /**
   * Renders models in render layers during the main pass.
   * @param {Array<RenderModel>} models - Array of RenderModel instances
   */
  renderLayerModels(models) {
    for (const model of models) {
      // Get all render batches for the model
      const batches = model.getIterator().getGeomScenes();

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        // Batches bypass nextBatch(), so applyVisibility() never sets MESH_RENDERFLAG.
        // Fall back to MESH_VISIBLE to avoid rendering nothing.
        batch.forceVisible = true;
        this._renderer.renderScenePart(batch, this.settings.renderEdges);
      }
    }
  }

  /**
   * Clears the main depth buffer.
   */
  clearMainDepth() {
    this._renderer.clearMainDepth();
  }

  /**
   * Renders overlays (legacy and new RenderLayer objects).
   * @param {{overlayScenes: Object, renderLayers: Array} | Object} overlaysOrData - Object containing overlay scenes and render layers or an object of (overlay) scenes
   * @param {Array<THREE.Light>} lights - Lights to use for rendering
   * @param {boolean} disableClear - Whether to disable clearing the overlay targets
   */
  renderOverlays(overlaysOrData, lights, disableClear) {
    // To keep backward compatibility, overlaysOrData can either be
    //     - an object of (overlay) scenes
    //     - OR an object with `overlayScenes` and `renderLayers` properties
    let overlays;
    let renderLayers;
    if (Object.prototype.hasOwnProperty.call(overlaysOrData, 'overlayScenes') ||
    Object.prototype.hasOwnProperty.call(overlaysOrData, 'renderLayers')) {
      overlays = overlaysOrData.overlayScenes;
      renderLayers = overlaysOrData.renderLayers;
    } else {
      overlays = overlaysOrData;
    }

    let haveOverlays = false;

    //NOTE: Disable clear was ported from WebGL for the SplitScreen extension which clears "manually" and calls
    //      renderOverlays per viewport. Maybe it can be removed again if a viewport-aware clear is implemented.
    if (!disableClear) {
      //TODO: only needs to be done when targets are not already clear (i.e. some overlay was previously rendered into them)
      this._renderer.clearOverlayTargets();
    }

    // Copy main depth into overlay depth buffer so overlays get correct occlusion
    // without polluting the progressive main pass depth across frames.
    if (this.settings.copyDepth) {
      this._renderer.copyDepthToOverlay();
    }

    // Render legacy overlay scenes.
    // Sort by scene.renderOrder (descending) to mirror WebGL's RenderContext.renderOverlays behavior.
    // Overlays use depthTest=false in many cases, so iteration order determines visual stacking.
    // Without this sort, scenes that opt into ordering via scene.renderOrder (e.g. Measure tool's
    // HighlightManager which sets renderOrder=100) get drawn in insertion order and can incorrectly
    // cover later-added overlays.
    let overlayArray = [];
    if (overlays && typeof overlays === 'object') {
      overlayArray = Object.values(overlays).sort((a, b) => {
        if (a.needSeparateDepth == b.needSeparateDepth) {
          return b.scene.renderOrder - a.scene.renderOrder;
        } else if (a.needSeparateDepth == true) {
          return 1;
        } else if (b.needSeparateDepth == true) {
          return -1;
        }
        return 0;
      });
    }

    for (let i = 0; i < overlayArray.length; ++i) {
      const p = overlayArray[i];
      const s = p.scene;
      const c = p.camera ? p.camera : this._camera;

      if (s.children.length) {

        if (!haveOverlays) {
          haveOverlays = true;
        }

        this._renderer.renderOverlay(s, c, p.materialPre, p.materialPost, this.settings.renderEdges, p.edgeColor, lights);
      }
    }

    // Render new RenderLayer objects
    if (renderLayers && renderLayers.length > 0) {
      for (const layer of renderLayers) {
        const hasContent = layer.scene || layer.models && layer.models.length > 0;
        if (!hasContent) continue;

        // Render scene if present
        if (layer.scene?.children.length) {
          this._renderer.renderOverlay(layer.scene, this._camera, null, null, this.settings.renderEdges, null, lights);
          haveOverlays = true;
        }

        // Render models if present
        if (layer.models?.length > 0) {
          this._renderer.renderOverlayModels(layer.models, this._camera, this.settings.renderEdges, lights);
          haveOverlays = true;
        }

        // Clear depth after rendering if requested
        if (layer.clearDepthAfter) {
          this._renderer.clearMainDepth();
        }
      }
    }

    this._renderer.getBlendSettings().setUseOverlay(haveOverlays);
  }

  // Takes color buffer, uses normal and depth buffer, puts SSAO shading into _ssaoTarget.
  // _postTarget1 is used along the way to ping-pong and do a separable blur on the results.
  computeSSAO(skipAOPass) {
    if (!skipAOPass && this.settings.sao) {
      this._renderer.getSAO().run(this._camera);
      this._renderer.getBlendSettings().setAOEnabled(true);
      //console.timeEnd("SAOblur");
    } else {
      // Ensure that any previous SSAO computation post-process target is not blended in.
      this._renderer.getBlendSettings().setAOEnabled(false);
    }
  }

  // Returns the final render target that presentBuffer eventually render to.
  getFinalTarget() {
    return this._offscreenTarget || null;
  }

  // userFinalPass is used by stereo rendering, giving the context to use for where the render should be put.
  // If no context is given, the default frame buffer is used.
  presentBuffer(userFinalPass, waitForDone) {
    // userFinalPass may either be set permanently or passed by a parameter by an overloaded RenderContext.
    this._renderer.present(this.settings.antialias, this._camera, waitForDone, userFinalPass || this._userFinalPass);
  }

  composeFinalFrame(skipAOPass, skipPresent, waitForDone) {
    //Apply the post pipeline and then show to screen.
    //Note that we must preserve the original color buffer
    //so that we can update it progressively

    if (!this._renderer)
    return;

    if (this._renderer.isMSAAEnabled()) {
      this._renderer.resolveMSAATargets();
    }

    // always called, so that useAO is set to 0 if not in use.
    this.computeSSAO(skipAOPass);

    if (!skipPresent)
    this.presentBuffer(undefined, waitForDone);

    //console.timeEnd("post");

  }

  cleanup() {

    if (this._renderer) {
      this._renderer.cleanup();
    }

    this._lastIdAtPixelsResults = {};
    this._idReadbackBuffers = {};
    this._modelIdReadbackBuffers = {};
  }

  setSize(w, h, force, suppress) {

    this._w = w;
    this._h = h;

    this.settings.logicalWidth = w;
    this.settings.logicalHeight = h;

    //Just a way to release the targets in cases when
    //we use a custom render context and don't need this one
    //temporarily
    if (w === 0 && h === 0 || !this._renderer) {
      this.cleanup();
      return;
    }

    var sw = 0 | w * this._renderer.getPixelRatio();
    var sh = 0 | h * this._renderer.getPixelRatio();

    this.settings.deviceWidth = sw;
    this.settings.deviceHeight = sh;

    // normally, render() calls setRenderTarget, which properly sets the size to be
    // the correct viewport for rendering. However, setAOEnabled also calls this
    // method, to allocate or deallocate the various SSAO buffers, etc. Because
    // post processing can increase the size of the target by 2x (code below),
    // we do not want to have setAOEnabled touch the renderer's setSize. Long and
    // short, setAOEnabled sends in "suppress" as true. LMV-2863
    if (!suppress) {
      if (this._isOffscreen) {
        // only set Viewport (which can be recovered later), but do not affect WebGLCanvas
        this._renderer.setViewport(0, 0, w, h);
      } else {
        this._renderer.setSize(w, h);
      }
    }

    //logger.log("width: " + sw + " height: " + sh);
  }

  getMaxAnisotropy() {
    return this._renderer ? this._renderer.getMaxAnisotropy() : 0;
  }

  // HACK: returns MRT flags required by this render context
  // so that the flags can be passed to the material manager
  mrtFlags() {
    return {
      mrtNormals: true,
      mrtIdBuffer: true
    };
  }

  getAntialiasing() {
    return this.settings.antialias;
  }

  initPostPipeline(useSAO, useFXAA) {

    //TODO: Do we want to move the IE check to higher level code?
    this.settings.sao = useSAO;
    this.settings.antialias = useFXAA;

    //Also reallocate the render targets
    this.setSize(this._w, this._h);
  }

  setClearColors(colorTop, colorBot) {
    if (!colorBot) {
      colorBot = colorTop;
    }

    this._renderer.getGradientPass().setClearColors(colorTop.x, colorTop.y, colorTop.z, colorBot.x, colorBot.y, colorBot.z);
  }

  /**
   * Turn on or off the use of the overlay alpha when computing the diffuse color's alpha
   * @param {Boolean} value - true to enable, false to disable.
   */
  useOverlayAlpha(value) {
    this._useOverlayAlpha = value;
  }

  setClearAlpha(alpha) {
    this._clearAlpha = alpha;
    this._renderer.getGradientPass().setAlpha(alpha);
    this._renderer.getEnvMapPass().setAlpha(alpha);
  }

  getClearAlpha() {
    return this._clearAlpha;
  }

  setAOEnabled(enabled) {
    this.settings.sao = enabled;
    this._oldSettings.sao = this.settings.sao;
    this._renderer.getBlendSettings().setAOEnabled(enabled);
    // recreate required buffers when sao is turned on; do not reset rendering size
    this.setSize(this._w, this._h, false, true);
  }

  /**
   * @param {Number|undefined} radius - SAO radius in model units
   * @param {Number|undefined} intensity - SAO intensity (default 1.0)
   * @param {Number|undefined} blendBias - Fixed bias added to ambient occlusion factor (effectively increases brightness)
   */
  setAOOptions(radius, intensity, blendBias) {
    let bias;
    if (radius !== undefined) {
      bias = 0.01; //TODO: should probably not be hardcoded here, but deeper down
    }
    this._renderer.getSAO().setAOOptions(radius, bias, intensity);

    if (blendBias !== undefined) {
      this._renderer.getBlendSettings().setAOBias(blendBias);
    }
  }

  getAOEnabled() {
    return this.settings.sao;
  }

  /**
   * @returns {number}
   */
  getAORadius() {
    return this._renderer.getSAO().getAOOptions().radius;
  }

  /**
   * @returns {number}
   */
  getAOIntensity() {
    return this._renderer.getSAO().getAOOptions().intensity;
  }

  /**
   * @returns {number}
   */
  getAOBias() {
    return this._renderer.getSAO().getAOOptions().bias;
  }

  /**
   * @returns {number}
   */
  getAOBlendBias() {
    return this._renderer.getBlendSettings().getAOBias();
  }

  setCubeMap(map) {
    this._renderer.getEnvMapPass().setCubeMap(map);
    this._renderer.setEnvMapEncoding(map);

    if (!map)
    this.toggleEnvMapBackground(false);
  }

  setEnvRotation(rotation) {
    this._envRotation = rotation;
    this._renderer.setEnvRotation(rotation);
  }

  getEnvRotation() {
    return this._envRotation;
  }

  setEnvExposure(exposure) {
    this._renderer.getEnvMapPass().setEnvExposure(exposure);
    this._exposure = exposure;
  }

  setTonemapExposureBias(bias) {
    this._exposureBias = bias;

    this._renderer.getEnvMapPass().setExposureBias(bias);
  }

  getExposureBias() {
    return this._exposureBias;
  }

  //Required for switching camera for stereo rendering
  setCamera(camera) {
    this._camera = camera;
  }

  setTonemapMethod(value) {
    this._tonemapMethod = value;
    this._renderer.setTonemapMethod(value);
    this._renderer.getEnvMapPass().setTonemapMethod(value);
  }

  getToneMapMethod() {
    return this._tonemapMethod;
  }

  toggleTwoSided(isTwoSided) {
    this.settings.haveTwoSided = isTwoSided;
  }

  toggleEdges(state) {
    this.settings.renderEdges = state;
    this._oldSettings.renderEdges = state; // avoid settings from outside to be overwritten if triggered before exit2DMode switch.
  }

  toggleEnvMapBackground(value) {
    this.settings.envMapBg = value;
  }

  //Returns the value of the ID buffer at the given
  //viewport location. Note that the viewport location is in
  //OpenGL-style coordinates [-1, 1] range.
  //If the optional third parameter is passed in, it's assume to be a two integer array-like,
  //and the extended result of the hit test (including model ID) is stored in it.
  idAtPixel(vpx, vpy, res) {
    return this.idAtPixels(vpx, vpy, 1, res);
  }

  // Helper function to copy array values
  _copyArray(srcArray, dstArray) {
    if (!srcArray || !dstArray) {
      return;
    }

    // Clean dst array.
    dstArray.length = 0;

    for (let i = 0; i < srcArray.length; i++) {
      dstArray[i] = srcArray[i];
    }
  }

  // Start the search at the center of the region and then spiral.
  _spiral(px, py, size, readbackBuffer, readbackBuffer2, result) {

    let id;
    let x = 0,y = 0;
    let dx = 0,dy = -1;
    let targetSize = this._renderer.getRenderTargets().getTargetSize();


    // Set initial values for result.
    // Result structure: [dbId, modelId, vpx, vpy, px, py]
    // vpx & vpy are the viewport hit coordinates.
    // px & py are the original center point in client coordinates - used for caching purposes.
    this._lastIdAtPixelsResults[size] = [-1, -1, null, null, px, py];

    for (let i = 0; i < size * size; i++) {

      // Translate coordinates with top left as (0, 0)
      const tx = x + (size - 1) / 2;
      const ty = y + (size - 1) / 2;
      if (tx >= 0 && tx <= size && ty >= 0 && ty <= size) {
        const index = tx + ty * size;
        const off = index * 4;
        // @todo: assign dataview to readbackBuffer once and use getUint32(offset, true)
        id = readbackBuffer[off + 3] << 24 | readbackBuffer[off + 2] << 16 | readbackBuffer[off + 1] << 8 | readbackBuffer[off];

        this._lastIdAtPixelsResults[size][0] = id;

        if (readbackBuffer2) {
          // Read modelId from readback buffer
          // @todo: assign dataview to readbackBuffer2 once and use getUint32(offset, true)
          let modelId = readbackBuffer2[off + 3] << 24 | readbackBuffer2[off + 2] << 16 |
          readbackBuffer2[off + 1] << 8 | readbackBuffer2[off];
          // In fallback mode, category is packed in upper 16 bits - mask to get actual modelId
          if (this._renderer.isCategoryHighlightingFallback()) {
            modelId = modelId & 0xFFFF;
          }
          this._lastIdAtPixelsResults[size][1] = modelId;
        }

        this._lastIdAtPixelsResults[size][2] = (px + tx) * 2 / targetSize[0] - 1; // hit x in viewport coords
        this._lastIdAtPixelsResults[size][3] = -((py + ty) * 2 / targetSize[1] - 1); // hit y in viewport coords

        // dbIds can be also negative (see F2d.currentFakeId). -1 is the only dbId that actually means "none".
        if (id !== -1) {
          break;
        }
      }

      if (x == y || x < 0 && x == -y || x > 0 && x == 1 - y) {
        const t = dx;
        dx = -dy;
        dy = t;
      }

      x += dx;
      y += dy;
    }

    // Copy cached values to output result array.
    this._copyArray(this._lastIdAtPixelsResults[size], result);

    return id;
  }

  idAtPixels(vpx, vpy, size, result) {

    // Make sure that size is an odd number. Even numbered size can’t be centered using integers.
    if (size % 2 === 0) {
      size += 1;
    }

    let rt = this._renderer.getRenderTargets();
    let sz = rt.getTargetSize();
    const px = (vpx + 1.0) * 0.5 * sz[0] - (size - 1) * 0.5 | 0;

    //TODO: viewport Y needs inversion because WebGPU uses y-down when reading from render targets
    const py = (-vpy + 1.0) * 0.5 * sz[1] - (size - 1) * 0.5 | 0;

    if (this._lastIdAtPixelsResults[size] && px === this._lastIdAtPixelsResults[size][4] && py === this._lastIdAtPixelsResults[size][5]) {

      // Copy cached values to output result array.
      this._copyArray(this._lastIdAtPixelsResults[size], result);

      // Return cached ID.
      return this._lastIdAtPixelsResults[size][0];
    }

    const bufferSize = 4 * size * size;

    if (!this._idReadbackBuffers[bufferSize]) {
      this._idReadbackBuffers[bufferSize] = new Uint8Array(bufferSize);
    }

    const readbackBuffer = this._idReadbackBuffers[bufferSize];

    let readbackBuffer2;
    if (!this._modelIdReadbackBuffers[bufferSize]) {
      this._modelIdReadbackBuffers[bufferSize] = new Uint8Array(bufferSize);
    }
    readbackBuffer2 = this._modelIdReadbackBuffers[bufferSize];

    //TODO: we need to expose the async API in an optional way and then transition code that
    //can work asynchronously to use it.
    // if (false) {
    // 	return rt.readIdTargetPixelsAsync(px, py, size, size, [readbackBuffer, readbackBuffer2]).then(() => {
    // 		return this._spiral(px, py, size, readbackBuffer, readbackBuffer2, result);
    // 	});
    // } else {
    rt.readIdTargetPixelsSyncOrFail(px, py, size, size, [readbackBuffer, readbackBuffer2]);
    return this._spiral(px, py, size, readbackBuffer, readbackBuffer2, result);
    //}
  }

  /**
   * @param {Number} vpx - Bottom left X-coordinate of the box, in NDC [-1, 1] over the canvas,
   * however as selection go outside of the canvas, these values can go beyond [-1, 1]
   * @param {Number} vpy - Bottom left Y-coordinate of the box, in NDC [-1, 1]
   * @param {Number} widthRatio - Width ratio of the box, relative to the canvas size, in [0, 1],
   * however as selection go outside of the canvas, these values can go beyond [0, 1]
   * @param {Number} heightRatio - Height ratio of the box, relative to the canvas size, in [0, 1]
   * @param {Array} results - Array to store the results in. Each result is an array of [objectId, modelId]
   * @returns {void}
   */
  idsAtPixelsBox(vpx, vpy, widthRatio, heightRatio, results) {
    const rt = this._renderer.getRenderTargets();
    // Device pixel ratio multiplied canvas size
    const targetSize = rt.getTargetSize();

    if (!targetSize[0] || !results) {
      return;
    }

    // Width and height of the box in pixels
    let width = Math.trunc(widthRatio * targetSize[0]);
    let height = Math.trunc(heightRatio * targetSize[1]);

    // px and py are the pixel coordinates of the top (!) left corner of the box
    let px = Math.trunc((vpx + 1.0) * 0.5 * targetSize[0]);
    const py_from_bottom = Math.trunc((vpy + 1.0) * 0.5 * targetSize[1]);
    let py = Math.trunc(targetSize[1] - py_from_bottom - height);

    // (0, 0) is is the top left and it increases to the right and down
    const left = Math.max(0, px);
    const top = Math.max(0, py);
    const right = Math.min(targetSize[0], px + width);
    const bottom = Math.min(targetSize[1], py + height);

    // Find the intersection of the box with the canvas
    if (left < right && top < bottom) {
      px = left;
      py = top;
      width = right - left;
      height = bottom - top;
    } else {
      return;
    }

    const buffers = [new Uint8Array(width * height * 4), new Uint8Array(width * height * 4)];
    if (!rt.readIdTargetPixelsSyncOrFail(px, py, width, height, buffers)) {
      return; // Not ready
    }

    const ids = {};
    const objectBuffer = buffers[0];
    const modelBuffer = buffers[1];

    for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex++) {
      const byteOffset = pixelIndex * 4;

      let objectId = objectBuffer[byteOffset + 2] << 16 | objectBuffer[byteOffset + 1] << 8 | objectBuffer[byteOffset];
      objectId = objectId << 8 >> 8;

      if (objectId !== -1 && objectId !== 0) {
        let modelId = modelBuffer[byteOffset + 1] << 8 | modelBuffer[byteOffset];
        modelId = modelId << 16 >> 16;

        const uniqueKey = `${objectId}-${modelId}`;
        if (!ids[uniqueKey]) {
          ids[uniqueKey] = true;
          results.push([objectId, modelId]);
        }
      }
    }
  }

  /**
   * {Number} vpx - OpenGL style X-coordinate [-1..1]
   * {Number} vpy - OpenGL style Y-coordinate [-1..1]
   */
  rolloverObjectViewport(vpx, vpy) {
    //_idRes[1] = 0; // Reset model-id to 0
    this.idAtPixel(vpx, vpy, this._idRes);
    return this.rolloverObjectId(this._idRes[0], null, this._idRes[1]);
  }

  getRollOverDbId() {
    return this._lastHighlightId;
  }

  getRollOverModelId() {
    return this._lastHighlightModelId;
  }

  // Update BlendShader configuration to specify which modelId(s)
  // are shown with rollOver highlight.
  _setHighlightModelId(modelId) {

    // No change => no work.
    if (modelId === this._lastHighlightModelId) {
      return false;
    }
    this._lastHighlightModelId = modelId;

    this._renderer.getBlendSettings().setHighlightModelId(modelId);

    return true;
  }

  // Configure BlendShader for highlighting the given object id
  _setHighlightObjectId(objId) {

    // No change => no work.
    if (objId === this._lastHighlightId) {
      return false;
    }
    this._lastHighlightId = objId;

    //console.log(objId, modelId);

    //Check if nothing was at that pixel -- 0 means object
    //that has no ID, ffffff (-1) means background, and both result
    //in no highlight.
    if (objId === -1) {
      objId = 0;
    }

    this._renderer.getBlendSettings().setHighlightObjectId(objId);

    return true;
  }

  // Configure rollover highlighting for objects or models
  //  @param {number}          objId
  //  @param {number|number[]} modelId            - One or multiple modelIds to be highlighted.
  //  @param {bool}            highlightFullModel - If true, the whole model is highlighted and the obId is ignored.
  _setRolloverHighlight(objId, modelId, highlightFullModel) {

    // An undefined modelId may happen if a) there is no MODEL_ID buffer or b) nothing is highlighted.
    modelId = modelId || 0;

    // apply new objId and modelId
    const objChanged = this._setHighlightObjectId(objId);
    const modelChanged = this._setHighlightModelId(modelId);

    // Only restart highlight fade on actual changes
    if (!objChanged && !modelChanged) {
      return;
    }

    this._lastObjChanged = true;

    this._renderer.getBlendSettings().setHighlightIntensity(0);
    this._lastObjTime = performance.now();
    this._renderer.getBlendSettings().setHighlightFullModel(highlightFullModel);

    return true;
  }

  /**
   * {Number} objId - Main Integer id to highlight. If it's not a leaf node,
   *                  then the dbIds (presumable all its children) will also be highlighed, too.
   * {Number} [dbIds] - OPTIONAL, id range to highlight.
   * {Number} [modelId] - OPTIONAL, id of the model containing the id range.
   */
  rolloverObjectId(objId, dbIds, modelId) {
    this._clearCategoryOnRollover && this.clearCategoryHighlighting();
    return this._setRolloverHighlight(objId, modelId, false);
  }

  // Roll-over highlighting for whole model. Requires modelId buffer.
  //  @param {number|number[]} modelId - One or more models to highlight.
  rollOverModelId(modelId) {
    this._clearCategoryOnRollover && this.clearCategoryHighlighting();
    return this._setRolloverHighlight(1, modelId, true);
  }

  // Note: Colored highlighting is currently only implemented for 3D. For 3D models, it has no effect.
  //
  // @param {THREE.Color} color - default is white
  // The color that is added to the actual fragment color on hover.
  // Default is white. Choosing a darker color reduces highlighting intensity.
  setRollOverHighlightColor(color) {
    this._renderer.getBlendSettings().setRolloverHighlightColor(color || THREE_WHITE);
  }

  setDbIdForEdgeDetection(objId, modelId) {
    this._renderer.getBlendSettings().setEdgeHighlightObjectId(objId, modelId);
  }

  clearCategoryHighlighting() {
    this._renderer.getBlendSettings().clearCategoryHighlighting();
  }

  /**
   * Controls whether rollover highlighting clears active category highlighting. When false (default), both
   * rollover and category highlighting coexist. When true, hovering over an object clears category highlighting.
   * @param {boolean} clear
   */
  setClearCategoryOnRollover(clear) {
    this._clearCategoryOnRollover = clear;
  }

  /**
   * Enables or disables category highlighting feature. When enabled and hardware supports it,
   * a 3rd ID-target is allocated. Otherwise, fallback mode is used (category packed into modelId).
   * This must be called before using setCategoryHighlighting() or setHighlightingCategoryMapping().
   * @param {boolean} enabled - Whether to enable category highlighting.
   */
  setCategoryHighlightingEnabled(enabled) {
    this._renderer.getRenderTargets().setCategoryHighlightingEnabled(enabled);
    this._renderer.refreshRenderTargets();
    this.settings.numIdTargets = this._renderer.getRenderTargets().getNumIdTargets();
  }

  /**
   * Returns whether category highlighting is currently enabled.
   * @returns {boolean}
   */
  isCategoryHighlightingEnabled() {
    return this._renderer.isCategoryHighlightingEnabled();
  }

  /**
   * Sets the highlighted category and comparison function for category-based hover-highlighting effect.
   * Note: setCategoryHighlightingEnabled(true) must be called first for this to have any effect.
   * @param {number} categoryValue - The highlighting category value to be used by the comparison function.
   * @param {CategoryHighlightingCompare} categoryCompare - Comparison function (see CategoryHighlightingCompare type)
   */
  setCategoryHighlighting(categoryValue, categoryCompare = CategoryHighlightingCompare.EXACT_MATCH) {

    const blendSettings = this._renderer.getBlendSettings();

    if (categoryValue) {
      this._setRolloverHighlight(0, 0, false);

      if (categoryValue !== blendSettings.getCategoryHighlightingValue()) {
        blendSettings.setCategoryHighlightIntensity(0);
        this._lastCategoryTime = performance.now();
        blendSettings.setCategoryHighlighting(categoryValue, categoryCompare);
      }
    } else {
      blendSettings.clearCategoryHighlighting();
    }

    blendSettings.setCategoryHighlighting(categoryValue, categoryCompare);
  }

  /** Forwards to Renderer.setEdgeColor. */
  setEdgeColor(colorAsVec4) {this._renderer.setEdgeColor(colorAsVec4);}

  /** Forwards to Renderer.setEdgeColorGhosted. */
  setEdgeColorGhosted(colorAsVec4) {this._renderer.setEdgeColorGhosted(colorAsVec4);}

  /** Forwards to Renderer.setDitheredGhosting. */
  setDitheredGhosting(dithered) {this._renderer.setDitheredGhosting(dithered);}

  /**
   * Whether the ghosted phase should render only edges (no face geometry).
   * For WebGPU this is always true — ghosted mode is edges-only by design.
   * @returns {boolean}
   */
  isGhostedEdgesOnly() {return true;}

  /** Not used by WebGPU — line geometry is handled by the edge pipeline's ghosted path. */
  getGhostLineMaterial() {return null;}

  /**
   * Prepare edge rendering state for the current render phase.
   * Sets both independent edge colors; depth-write is a no-op for WebGPU.
   *
   * @param {boolean} _isGhosted - Unused; WebGPU handles both colors independently.
   * @param {THREE.Vector4} mainColor - Edge color for normal geometry.
   * @param {THREE.Vector4} ghostedColor - Edge color for ghosted geometry.
   */
  prepareEdgesForPhase(_isGhosted, mainColor, ghostedColor) {
    this._renderer.setEdgeColor(mainColor);
    this._renderer.setEdgeColorGhosted(ghostedColor);
  }

  setEdgeDepthWriteEnabled(enabled) {

    // Nothing
  }
  /**
   * @deprecated just for backwards compatibility with RenderContext
   * @param {THREE.ColorRepresentation} color
   **/
  setSelectionColor(color) {
    this.setSelectionColorOverlay(color);
  }

  /**
   * Selection color for overlay-selection effect (usually used for single- or small selections)
   * @param {THREE.ColorRepresentation} color
   **/
  setSelectionColorOverlay(color) {
    // The selection color is gamma corrected using 2.0.
    var gamma = new THREE.Color(color);
    gamma.r = Math.pow(gamma.r, 2.0);
    gamma.g = Math.pow(gamma.g, 2.0);
    gamma.b = Math.pow(gamma.b, 2.0);
    this._renderer.getBlendSettings().setSelectionColor(gamma);
    this.settings.selectionColor = color;
  }

  /**
   *  Selection color for simpler colorization highlighting (SelectionType.Regular).
   *  Used for larger multi-selection, e.g., when selecting inner nodes in Model Browser.
   *  @param {THREE.ColorRepresentation} color
   **/
  setSelectionColorRegular(color) {
    var c = new THREE.Color(color);
    const intColor = colorToABGR(c);
    const uniforms = this._renderer.getRenderBatchUniforms().getCommonMaterialUniforms();
    uniforms.setSelectionColorInt(intColor);
  }

  setUnitScale(metersPerUnit) {
    let scaleFactor = this._unitScale / metersPerUnit;
    this._unitScale = metersPerUnit;

    this._renderer.getSAO().setUnitScale(scaleFactor);

    //console.log("change of scale", _saoPass.uniforms[ 'radius' ].value);
  }

  getUnitScale() {
    return this._unitScale;
  }

  getColorTarget() {
    const rt = this._renderer.getRenderTargets();
    return rt.getColorTarget();
  }

  /**
   * @returns {GPUTexture}
   */
  getOverlayTarget() {
    const rt = this._renderer.getRenderTargets();
    return rt.getOverlayTarget();
  }

  /**
   * @returns {GPUTexture} WebGPU texture
   */
  getIdTarget(index = 0) {
    const rt = this._renderer.getRenderTargets();
    return rt.getIdTarget(index);
  }

  /**
   * @returns {GPUTexture} Returns depth target used as input texture for rgb10-packed depth.
   */
  getViewDepthTarget() {
    const rt = this._renderer.getRenderTargets();
    return rt.getViewDepthTarget();
  }

  /**
   * @returns {GPUTexture|null}
   */
  getOffscreenTarget() {
    return this._renderer.getOffscreenTarget();
  }

  /**
   * @param {GPUTexture|null} target
   */
  setOffscreenTarget(target) {
    this._renderer.setOffscreenTarget(target);
  }

  /**
   * @returns {WebGLFramebuffer} Currently bound framebuffer for this context
   */
  getCurrentFramebuffer() {
    return this._renderer.getCurrentFramebuffer();
  }

  /**
   * @param {Vector4[]} planes
   */
  setCutPlanes(planes) {
    this._renderer.getIBL().setCutPlanes(planes);
  }

  /**
   * Sets level cutplanes applied only to materials with the includeLevelCutPlanes property.
   * Pass null or an empty array to clear. Level cutplanes persist across model loads; callers are responsible for clearing them.
   * @param {Vector4[]|null} planes
   */
  setLevelCutPlanes(planes) {
    this._renderer.getIBL().setLevelCutPlanes(planes);
  }

  /**
   * @param {number} value Either 0.0 (no swap) or 1.0 (swap).
   */
  setSwapBlackAndWhite(value) {
    this.settings.swapBlackAndWhite = value;
    this._renderer.getMainPass().setSwapBlackAndWhite(this.settings.swapBlackAndWhite);
  }

  // Returns a state object combines various configuration settings that may be modified from outside.
  getConfig() {

    let clearColors = this._renderer.getGradientPass().getClearColors();

    return {
      renderEdges: this.settings.renderEdges,
      envMapBackground: this.settings.envMapBg,
      envMap: this._renderer.getEnvMapPass().getCubeMap(),
      envExposure: this._exposure,
      toneMapExposureBias: this._exposureBias,
      envRotation: this.getEnvRotation(),
      tonemapMethod: this._tonemapMethod,
      //clearColor:, // WebGL only
      clearColorTop: new THREE.Vector3(clearColors[0], clearColors[1], clearColors[2]),
      clearColorBottom: new THREE.Vector3(clearColors[3], clearColors[4], clearColors[5]),
      clearAlpha: this._clearAlpha,
      useOverlayAlpha: this._useOverlayAlpha,
      aoEnabled: this.getAOEnabled(),
      aoRadius: this.getAORadius(),
      aoIntensity: this.getAOIntensity(),
      aoBlendBias: this.getAOBlendBias(), // WebGPU only
      twoSided: this.settings.haveTwoSided,
      //edgeColor: // WebGL only
      unitScale: this.getUnitScale(),
      // is2D: // WebGL only
      antialias: this.getAntialiasing(),
      // idMaterial: // WebGL only
      selectionColor: this.settings.selectionColor,
      swapBlackAndWhite: this.settings.swapBlackAndWhite // WebGPU only
    };
  }

  applyConfig(config) {
    this.toggleEdges(config.renderEdges);
    this.toggleEnvMapBackground(config.envMapBackground);
    this.setCubeMap(config.envMap);
    this.setEnvExposure(config.envExposure);
    this.setTonemapExposureBias(config.toneMapExposureBias);
    this.setEnvRotation(config.envRotation);
    this.setTonemapMethod(config.tonemapMethod);
    this.toggleTwoSided(config.twoSided);
    this.setUnitScale(config.unitScale);
    this.setAOOptions(config.aoRadius, config.aoIntensity, config.aoBlendBias);
    this.setSwapBlackAndWhite(config.swapBlackAndWhite);

    if (config.clearColor) {
      this.setClearColors(config.clearColor);
    } else {
      this.setClearColors(config.clearColorTop, config.clearColorBottom);
    }
    this.setClearAlpha(config.clearAlpha);
    this.useOverlayAlpha(config.useOverlayAlpha);

    // Toggling SAO or antialiasing needs to reinitialize post pipeline.
    // Note: In theory, it may happen that initPostPipeline runs twice if there
    //       was already a 2D/3D mode switch above. But that's not really a frequent case.
    var saoChanged = config.aoEnabled != this.getAOEnabled();
    var antialiasChanged = config.antialias != this.getAntialiasing();
    if (saoChanged || antialiasChanged) {
      this.initPostPipeline(config.aoEnabled, config.antialias);
    }
  }

  /**
   * @param {THREE.Material|undefined} idMaterial
   * @param {THREE.Color} selectionColor
   */
  enter2DMode(idMaterial, selectionColor) {
    // Note: Some differences to the WebGL case here:
    //   - idMaterial is ignored: It was only needed for the "No MRT-support" case, which we can ignore in WebGPU
    //   - It ensured that idBuffer select is active (which is currently always the case anyway)
    this._oldSettings.sao = this.settings.sao;
    this._oldSettings.antialias = this.settings.antialias;
    this._oldSettings.renderEdges = this.settings.renderEdges;
    this._oldSettings.selectionColor = this.settings.selectionColor;

    this.setSelectionColor(selectionColor);
    // Note: If edges are active, the edge rendering pass assumes all main scene geometry provides edge indices.
    // Any geometry without edge indices would just re-rendered using the edge shader - which just results in artifacts.
    // Therefore, disable edge rendering for 2D mode.
    this.settings.renderEdges = false;

    this._renderer.getBlendSettings().setIs2d(true);

    this.initPostPipeline(false, false);
  }

  exit2DMode() {
    this.settings.renderEdges = this._oldSettings.renderEdges;

    if (this._oldSettings.selectionColor) {
      this.setSelectionColor(this._oldSettings.selectionColor);
    }

    this._renderer.getBlendSettings().setIs2d(false);

    this.initPostPipeline(this._oldSettings.sao, this._oldSettings.antialias);
  }

  getBlendHighlightIntensity() {
    return this._renderer.getBlendSettings().getHighlightIntensity();
  }

  setBlendHighlightIntensity(intensity) {
    this._renderer.getBlendSettings().setHighlightIntensity(intensity);
  }

  spatialFilterForRollOverSupported() {
    return this.settings.sao;
  }

  setSpatialFilterForRollOver(filter, zRange) {
    if (zRange !== undefined && !this.spatialFilterForRollOverSupported()) {
      console.warn('Spatial filter for mouse-over not supported');
      return;
    }
    this._renderer.getPostPass().enableSpatialFilter();
    this._renderer.getBlendSettings().setZRange(...zRange);
  }

  disableSpatialFilterForRollOver() {
    this._renderer.getPostPass().disableSpatialFilter();
  }

  // Workaround helper for tests: readIdTargetPixelsSyncOrFail() may fail,
  // so that, e.g., a scripted 2D selection may fail if the id buffer isn't cached yet.
  // The returned promise resolves if any pending or outstanding idBuffer readback is finished.
  //
  // Note: This function only makes sure that pending readbacks are finished.
  //       So, sync id readback is only guaranteed to work afterwards as long as
  //       no changes (camera/model/visibility) happen after calling this function that invalidate the id buffer .
  //
  // It's not great and rather a workaround, but the async problems are a known major TechDebt issue that is out-of-scope short-term (see VIZX-XXX)
  //
  // @returns {Promise} May fail if the canvas is resized during readback.
  waitForIdReadback() {
    // make sure that caching readback is initiated. This usually happens already on first mouse-hover,
    // but in a scripted test scenario, the readback may never happen otherwise.
    const targets = this._renderer.getRenderTargets();
    const success = targets.readIdTargetPixelsSyncOrFail(0, 0, 1, 1);
    if (success) {
      // Best case - cache is ready to read
      return Promise.resolve();
    }

    // Unlikely, but in case that a previous call is waiting already, just return same promise.
    if (this._waitForIdReadbackPromise) {
      return this._waitForIdReadbackPromise;
    }

    this._waitingForIdReadback = new Promise((resolve, reject) => {
      targets.onIdReadbackCacheFinished = (success) => {
        // cleanup afterwards
        targets.onIdReadbackCacheFinished = null;
        this._waitingForIdReadback = null;

        success ? resolve() : reject();
      };
    });

    return this._waitingForIdReadback;
  }

  /**
   * Canvas with a webgpu context and texture.
   * @typedef {Object} WebGpuCanvas
   * @property {HTMLCanvasElement} canvas - Canvas with a webpgu context
   * @property {GPUTexture} texture - The canvas's texture
   * @property {function()} cleanup - This must be called when finished with
   *     the canvas. It will cleanup the webgpu canvas context and texture.
   */
  /**
   * Use Renderer's GPUDevice to create a WebGPU canvas.
   * @param {number} w
   * @param {number} h
   * @returns {WebGpuCanvas}
   */
  createWebGpuCanvas(w, h) {
    var tmpCanvas = getGlobal().document.createElement('canvas');
    tmpCanvas.width = w;
    tmpCanvas.height = h;

    /** @type {GPUCanvasContext} */
    const context = tmpCanvas.getContext("webgpu");
    context.configure({
      device: this._renderer.getDevice(),
      format: navigator.gpu.getPreferredCanvasFormat(),
      usage: GPUTextureUsage.RENDER_ATTACHMENT |
      GPUTextureUsage.COPY_SRC | GPUTextureUsage.COPY_DST,
      alphaMode: 'premultiplied'
    });
    return {
      canvas: tmpCanvas,
      texture: context.getCurrentTexture(),
      cleanup: () => {
        context.unconfigure();
      }
    };
  }

  /**
   * Copies a WebGPU target into a temporary WebGPU canvas.
   * @param {GPUTexture} target
   * @returns {Promise<WebGpuCanvas>}
   */
  async targetToCanvas(target) {
    const dst = this.createWebGpuCanvas(target.width, target.height);

    const device = this._renderer.getDevice();
    const encoder = device.createCommandEncoder({ label: 'canvas copy encoder' });
    encoder.copyTextureToTexture(
      { texture: target, origin: [0, 0, 0] },
      { texture: dst.texture, origin: [0, 0, 0] },
      [target.width, target.height]);

    device.queue.submit([encoder.finish()]);
    await device.queue.onSubmittedWorkDone();

    return dst;
  }

}